import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useApp } from '../context/AppContext';
import useAppStore from '../store/useAppStore';
import { BADGES } from '../data/badges';
import db from '../data/db';
import { openExternal, TERMS_URL, PRIVACY_URL } from '../utils/openExternal';
import {
  FiArrowLeft, FiLogOut, FiTrash2, FiFileText, FiShield, FiZap,
  FiAward, FiTarget, FiEdit2, FiCheck, FiX,
} from 'react-icons/fi';

const AVATAR_CHOICES = ['🎓', '📚', '🧠', '⚡', '🦊', '🐼', '🚀', '🌟', '🦉', '🎯', '🔥', '🌱'];

export default function Profile() {
  const navigate = useNavigate();
  const { user, signOut, deleteAccount } = useAuth();
  const { isPremium, tier, showPricing } = useSubscription();
  const { streak } = useApp();

  const displayName = useAppStore(s => s.displayName);
  const avatarEmoji = useAppStore(s => s.avatarEmoji);
  const dailyGoal = useAppStore(s => s.dailyGoal);
  const unlockedBadges = useAppStore(s => s.unlockedBadges);
  const setDisplayName = useAppStore(s => s.setDisplayName);
  const setAvatarEmoji = useAppStore(s => s.setAvatarEmoji);
  const setDailyGoal = useAppStore(s => s.setDailyGoal);

  const email = user?.signInDetails?.loginId || '';
  const fallbackName = email.split('@')[0] || 'You';
  const shownName = displayName || fallbackName;

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(shownName);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [todayStudied, setTodayStudied] = useState(0);
  const [totals, setTotals] = useState({ cards: 0, minutes: 0, accuracy: 0 });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const avatarRef = useRef(null);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setShowAvatarPicker(false);
      }
    }
    if (showAvatarPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAvatarPicker]);

  async function loadStats() {
    const allStats = await db.stats.toArray();
    const today = new Date().toISOString().split('T')[0];
    const todayStat = allStats.find(s => s.date === today);
    setTodayStudied(todayStat?.cardsStudied || 0);

    const cards = allStats.reduce((sum, s) => sum + (s.cardsStudied || 0), 0);
    const minutes = allStats.reduce((sum, s) => sum + (s.minutesStudied || 0), 0);
    const correct = allStats.reduce((sum, s) => sum + (s.correctCards || 0), 0);
    setTotals({
      cards,
      minutes: Math.round(minutes),
      accuracy: cards > 0 ? Math.round((correct / cards) * 100) : 0,
    });
  }

  function saveName() {
    const trimmed = nameDraft.trim();
    setDisplayName(trimmed);
    setEditingName(false);
    if (trimmed) toast.success('Name updated');
  }

  function pickAvatar(emoji) {
    setAvatarEmoji(emoji);
    setShowAvatarPicker(false);
  }

  async function handleDeleteAccount() {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success('Account deleted');
      navigate('/login', { replace: true });
    } catch {
      setDeleting(false);
    }
  }

  const goalPct = Math.min(100, Math.round((todayStudied / dailyGoal) * 100));
  const unlockedCount = Object.keys(unlockedBadges).length;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/')}
            style={{ marginBottom: 'var(--space-sm)' }}
          >
            <FiArrowLeft /> Back
          </button>
          <h1>Profile</h1>
        </div>
      </div>

      {/* Identity card */}
      <div
        style={{
          padding: 'var(--space-lg)',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          gap: 'var(--space-lg)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div ref={avatarRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowAvatarPicker(p => !p)}
            title="Change avatar"
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'var(--gradient-primary)',
              border: 'none',
              fontSize: 36,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {avatarEmoji}
          </button>
          {showAvatarPicker && (
            <div
              style={{
                position: 'absolute',
                top: 80,
                left: 0,
                zIndex: 10,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-primary)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-sm)',
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 36px)',
                gap: 4,
                boxShadow: 'var(--shadow-lg)',
              }}
            >
              {AVATAR_CHOICES.map(e => (
                <button
                  key={e}
                  onClick={() => pickAvatar(e)}
                  style={{
                    width: 36,
                    height: 36,
                    fontSize: 22,
                    border: avatarEmoji === e ? '2px solid var(--accent-primary)' : '1px solid transparent',
                    background: 'transparent',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 200 }}>
          {editingName ? (
            <div style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
              <input
                type="text"
                value={nameDraft}
                onChange={e => setNameDraft(e.target.value)}
                maxLength={40}
                autoFocus
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  background: 'var(--bg-input, var(--bg-elevated))',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-primary)',
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') { setEditingName(false); setNameDraft(shownName); }
                }}
              />
              <button className="btn btn-sm btn-primary" onClick={saveName}><FiCheck /></button>
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => { setEditingName(false); setNameDraft(shownName); }}
              >
                <FiX />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <h2 style={{ margin: 0 }}>{shownName}</h2>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { setEditingName(true); setNameDraft(shownName); }}
                title="Edit name"
              >
                <FiEdit2 />
              </button>
              {isPremium && (
                <span className="profile-premium-badge" style={{ marginLeft: 'auto' }}>Premium</span>
              )}
            </div>
          )}
          <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0' }}>{email}</p>
          <p style={{ color: 'var(--text-tertiary)', margin: '2px 0 0', fontSize: '0.85rem' }}>
            {tier === 'free' ? 'Free plan' : `${tier} plan`}
          </p>
        </div>
      </div>

      {/* Quick stats row */}
      <div className="stats-grid" style={{ marginBottom: 'var(--space-lg)' }}>
        <div className="stat-card">
          <div className="stat-card-icon">🔥</div>
          <div className="stat-card-value">{streak.current}</div>
          <div className="stat-card-label">Day Streak</div>
          <div className="stat-card-sub">Best: {streak.longest}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><FiAward /></div>
          <div className="stat-card-value">{unlockedCount}</div>
          <div className="stat-card-label">Badges</div>
          <div className="stat-card-sub">of {BADGES.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon">📊</div>
          <div className="stat-card-value">{totals.cards}</div>
          <div className="stat-card-label">Cards Reviewed</div>
          <div className="stat-card-sub">{totals.accuracy}% accuracy</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon">⏱️</div>
          <div className="stat-card-value">{totals.minutes}</div>
          <div className="stat-card-label">Minutes Studied</div>
          <div className="stat-card-sub">all time</div>
        </div>
      </div>

      {/* Daily goal */}
      <div className="stats-section">
        <h2><FiTarget /> Daily Goal</h2>
        <div
          style={{
            padding: 'var(--space-lg)',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-sm)' }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              Today: <strong>{todayStudied}</strong> / {dailyGoal} cards
            </span>
            <span style={{ color: goalPct >= 100 ? 'var(--success)' : 'var(--text-tertiary)' }}>
              {goalPct}%
            </span>
          </div>
          <div
            style={{
              height: 10,
              background: 'var(--bg-elevated)',
              borderRadius: 'var(--radius-sm)',
              overflow: 'hidden',
              marginBottom: 'var(--space-md)',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${goalPct}%`,
                background: goalPct >= 100 ? 'var(--success)' : 'var(--gradient-primary)',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Target:</span>
            <input
              type="number"
              min="1"
              max="500"
              value={dailyGoal}
              onChange={e => setDailyGoal(e.target.value)}
              style={{
                width: 80,
                padding: '6px 10px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-primary)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
              }}
            />
            <span style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>cards / day</span>
          </label>
        </div>
      </div>

      {/* Badges */}
      <div className="stats-section">
        <h2><FiAward /> Badges</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 'var(--space-md)',
          }}
        >
          {BADGES.map(badge => {
            const unlocked = !!unlockedBadges[badge.id];
            return (
              <div
                key={badge.id}
                title={unlocked ? `Unlocked ${new Date(unlockedBadges[badge.id]).toLocaleDateString()}` : 'Locked'}
                style={{
                  padding: 'var(--space-md)',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 'var(--radius-lg)',
                  textAlign: 'center',
                  opacity: unlocked ? 1 : 0.4,
                  filter: unlocked ? 'none' : 'grayscale(0.8)',
                  transition: 'opacity 0.2s, transform 0.2s',
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 'var(--space-xs)' }}>{badge.icon}</div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{badge.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                  {badge.description}
                </div>
                <div
                  style={{
                    marginTop: 'var(--space-xs)',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    color: badge.tier === 'gold' ? '#f5c518'
                      : badge.tier === 'silver' ? '#9aa0a6'
                      : '#b08560',
                    fontWeight: 700,
                  }}
                >
                  {badge.tier}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Account section */}
      <div className="stats-section">
        <h2>Account</h2>
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          {!isPremium && (
            <button
              className="profile-dropdown-item profile-upgrade-item"
              onClick={() => showPricing('generic')}
              style={{ width: '100%', textAlign: 'left' }}
            >
              <FiZap />
              <span>Upgrade to Premium</span>
            </button>
          )}
          <button
            type="button"
            className="profile-dropdown-item"
            onClick={() => openExternal(PRIVACY_URL)}
            style={{ width: '100%', textAlign: 'left' }}
          >
            <FiShield />
            <span>Privacy Policy</span>
          </button>
          <button
            type="button"
            className="profile-dropdown-item"
            onClick={() => openExternal(TERMS_URL)}
            style={{ width: '100%', textAlign: 'left' }}
          >
            <FiFileText />
            <span>Terms of Use</span>
          </button>
          <div style={{ borderTop: '1px solid var(--border-secondary)' }} />
          <button
            className="profile-dropdown-item danger"
            onClick={signOut}
            style={{ width: '100%', textAlign: 'left' }}
          >
            <FiLogOut />
            <span>Sign Out</span>
          </button>
          <div style={{ borderTop: '1px solid var(--border-secondary)' }} />
          {!showDeleteConfirm ? (
            <button
              className="profile-dropdown-item danger"
              onClick={() => setShowDeleteConfirm(true)}
              style={{ width: '100%', textAlign: 'left' }}
            >
              <FiTrash2 />
              <span>Delete Account</span>
            </button>
          ) : (
            <div className="profile-delete-confirm">
              <p className="profile-delete-warning">
                This action is permanent and cannot be undone. All your decks, cards,
                review history, and account data will be deleted.
              </p>
              <label className="profile-delete-label">
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                type="text"
                className="profile-delete-input"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                disabled={deleting}
              />
              <div className="profile-delete-actions">
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => { setShowDeleteConfirm(false); setConfirmText(''); }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={confirmText !== 'DELETE' || deleting}
                >
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
